import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  signal,
  ViewChild,
  effect,
  OnDestroy,
  Renderer2,
  ChangeDetectorRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { DropdownService } from 'src/utils/dropdown.service';
import { TruncateTooltipDirective } from 'src/ui/directives/truncate-tooltip.directive';

export interface SearchableOption {
  value: string | number;
  label: string;
  options?: SearchableOption[];
  isGroup?: boolean;
  isStrong?: boolean;
}

@Component({
  selector: 'searchable-dropdown',
  imports: [CommonModule, FormsModule, TruncateTooltipDirective],
  templateUrl: './searchable-dropdown.component.html',
  standalone: true,
})
export class SearchableDropdownComponent implements OnDestroy {
  private _options: SearchableOption[] = [];
  @Input()
  set options(v: SearchableOption[]) {
    this._options = v || [];
    if (this._selectedOption) {
      const exists = this._options.some(
        (o) => o.value === this._selectedOption?.value
      );
      if (!exists) {
        this.searchTerm = this._selectedOption.label ?? '';
      }
    }
  }
  get options(): SearchableOption[] {
    return this._options;
  }

  @Input() icon: string = 'fi fi-rr-filter';
  @Input() placeholder: string = 'Seleccione opciones...';
  @Input() allowCreateFromInput: boolean = true;
  @Output() optionSelectedChange = new EventEmitter<SearchableOption>();
  @Output() inputChanged = new EventEmitter<string | null>();

  private _selectedOption: SearchableOption | null = null;
  @Input()
  set selectedOption(val: SearchableOption | null) {
    this._selectedOption = val ?? null;
    if (this._selectedOption && this._selectedOption.label) {
      this.searchTerm = this._selectedOption.label;
    } else {
      this.searchTerm = '';
    }
  }
  get selectedOption(): SearchableOption | null {
    return this._selectedOption;
  }

  @Input() dropdownHeight: string = '32px';
  @Input() dropdownBorderRadius: string = '8px';
  @Input() fontSize: string = '12px';
  @Input() fontWeight: string = '500';
  @Input() placeholderFontSize: string = '12px';
  @Input() placeholderFontWeight: string = '400';
  @Input() radiusSearch: string = '8px';

  private searchTermSubject = new BehaviorSubject<string>('');
  searchTerm$ = this.searchTermSubject.asObservable();
  isOpen = signal(false);
  dropdownId: string;

  @ViewChild('selectContainer', { static: false }) selectContainer!: ElementRef;
  @ViewChild('panel', { static: false }) panelRef!: ElementRef<HTMLDivElement>;

  // para mover/volver a insertar el panel
  private placeholderNode?: Comment | null = null;
  private moved = false;
  private onWindowResize = () => this.dropdownService.closeDropdown();
  private onWindowScroll = () => this.positionPanel();
  private onAncestorScroll = () => this.dropdownService.closeDropdown();
  private scrollableAncestors: HTMLElement[] = [];

  constructor(
    private dropdownService: DropdownService,
    private renderer: Renderer2,
    private host: ElementRef,
    private cd: ChangeDetectorRef
  ) {
    this.dropdownId = `searchable-${Math.random().toString(36).substr(2, 9)}`;

    // efecto existente: escucha el dropdownService y abre/cierra el flag
    effect(() => {
      const openDropdownId = this.dropdownService.getOpenDropdown()();
      if (openDropdownId === this.dropdownId) {
        this.isOpen.set(true);
      } else {
        this.isOpen.set(false);
        if (this._selectedOption) {
          this.searchTerm = this._selectedOption.label;
        } else {
          this.searchTerm = '';
        }
      }
    });

    // reacciona al cambio de isOpen para mover/posicionar panel
    effect(() => {
      if (this.isOpen()) {
        // dejar que el template renderice primero
        setTimeout(() => {
          this.openPanel();
        }, 0);
      } else {
        // cerrar
        this.closePanel();
      }
    });
  }

  get searchTerm(): string {
    return this.searchTermSubject.value;
  }

  set searchTerm(value: string) {
    this.searchTermSubject.next(value ?? '');
  }

  get filteredOptions() {
    const searchValue = this.searchTermSubject.value || '';
    const lowerSearch = searchValue.toLowerCase();

    if (!searchValue) {
      return this.options;
    }

    return this.options
      .map((option) => {
        if (option.options && option.options.length > 0) {
          const filteredSubOptions = option.options.filter((subOpt) =>
            subOpt.label.toLowerCase().includes(lowerSearch)
          );

          if (filteredSubOptions.length > 0) {
            return {
              ...option,
              options: filteredSubOptions,
            };
          }

          if (option.label.toLowerCase().includes(lowerSearch)) {
            return option;
          }

          return null;
        }

        return option.label.toLowerCase().includes(lowerSearch) ? option : null;
      })
      .filter((opt) => opt !== null) as SearchableOption[];
  }

  toggleDropdown() {
    const currentlyOpen = this.isOpen();
    this.dropdownService.toggleDropdown(this.dropdownId);

    if (!currentlyOpen) {
      if (this._selectedOption && this._selectedOption.label) {
        this.searchTerm = this._selectedOption.label;
      }
    }
  }

  clearSearch() {
    this.searchTerm = '';
    this.inputChanged.emit(null);
  }

  onUserInput(event: Event) {
  const v = (event.target as HTMLInputElement).value;
  this.searchTerm = v;
  const trimmed = v == null ? '' : String(v).trim();
  if (trimmed === '') {
    this._selectedOption = null;
    this.optionSelectedChange.emit(undefined);
  }
  
  this.inputChanged.emit(trimmed === '' ? null : v);
}

  selectOption(option: SearchableOption) {
    const exists = this._options.some((o) => o.value === option.value);
    if (!exists && !this.allowCreateFromInput) {
      this.searchTerm = option.label;
      this.inputChanged.emit(option.label);
      this.dropdownService.closeDropdown();
      return;
    }
    this._selectedOption = option;
    this.searchTerm = option.label;
    this.optionSelectedChange.emit(option);
    this.inputChanged.emit(option.label);
    this.dropdownService.closeDropdown();
  }

  getDisplayParentGroupLabel(): string | null {
    const parentGroup = this._options.find(
      (opt) =>
        opt.options &&
        opt.options.some((sub) => sub.value === this._selectedOption?.value)
    );

    return parentGroup?.label || null;
  }
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // si el click NO está dentro del contenedor original ni dentro del panel movido -> cerrar
    const insideTrigger = !!target.closest('.select-container');
    const insidePanel = !!target.closest('.searchable-dropdown-panel');
    if (!insideTrigger && !insidePanel) {
      this.dropdownService.closeDropdown();
    }
  }

  // Cerrar también en mousedown/touchstart para capturar temprano el evento
  @HostListener('document:mousedown', ['$event'])
  onMouseDownOutside(event: MouseEvent) {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    const insideTrigger = !!target.closest('.select-container');
    const insidePanel = !!target.closest('.searchable-dropdown-panel');
    if (!insideTrigger && !insidePanel) {
      this.dropdownService.closeDropdown();
    }
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchStartOutside(event: TouchEvent) {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    const insideTrigger = !!target.closest('.select-container');
    const insidePanel = !!target.closest('.searchable-dropdown-panel');
    if (!insideTrigger && !insidePanel) {
      this.dropdownService.closeDropdown();
    }
  }

  private findScrollableAncestors(element: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = [];
    let parent = element.parentElement;

    while (parent) {
      const hasOverflow = this.hasScrollableOverflow(parent);
      if (hasOverflow) {
        ancestors.push(parent);
      }
      parent = parent.parentElement;
    }

    return ancestors;
  }

  private hasScrollableOverflow(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    const overflowX = style.overflowX;
    const overflowY = style.overflowY;

    return (
      (overflowX === 'scroll' ||
        overflowX === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'auto') &&
      (element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth)
    );
  }

  private openPanel() {
    // si aún no hay panelRef (no renderizó aún) o ya movido -> posicionar y salir
    if (!this.panelRef) return;
    const panelEl = this.panelRef.nativeElement;
    if (!panelEl) return;

    if (this.moved) {
      // ya está en body -> sólo reposicionar
      this.positionPanel();
      return;
    }

    // crear placeholder y mover panel al body
    const parent = panelEl.parentNode as Node;
    if (!parent) return;

    this.placeholderNode = document.createComment('dropdown-placeholder');
    parent.replaceChild(this.placeholderNode, panelEl);
    document.body.appendChild(panelEl);

    panelEl.classList.add('searchable-dropdown-panel');
    panelEl.style.position = 'fixed';
    panelEl.style.zIndex = '11000';
    panelEl.style.width = `${
      (
        this.selectContainer?.nativeElement as HTMLElement
      )?.getBoundingClientRect().width
    }px`;
    this.moved = true;

    // detectar ancestros scrolleables
    this.scrollableAncestors = this.findScrollableAncestors(
      this.selectContainer?.nativeElement as HTMLElement
    );

    // listeners
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('scroll', this.onWindowScroll, true); // capture to handle ancestor scrolls

    // agregar listeners a ancestros scrolleables
    this.scrollableAncestors.forEach((ancestor) => {
      ancestor.addEventListener('scroll', this.onAncestorScroll);
    });

    // posicionar
    this.positionPanel();
    this.cd.detectChanges();
  }

  private closePanel() {
    if (!this.panelRef) return;
    const panelEl = this.panelRef.nativeElement;
    if (!panelEl) return;

    if (!this.moved) return;

    // remover listeners
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('scroll', this.onWindowScroll, true);

    // remover listeners de ancestros scrolleables
    this.scrollableAncestors.forEach((ancestor) => {
      ancestor.removeEventListener('scroll', this.onAncestorScroll);
    });
    this.scrollableAncestors = [];

    // quitar estilos inline que pusimos
    panelEl.style.position = '';
    panelEl.style.top = '';
    panelEl.style.left = '';
    panelEl.style.zIndex = '';
    panelEl.style.width = '';
    panelEl.classList.remove('searchable-dropdown-panel');

    // volver a insertar en su lugar original
    if (this.placeholderNode && this.placeholderNode.parentNode) {
      this.placeholderNode.parentNode.replaceChild(
        panelEl,
        this.placeholderNode
      );
    }
    this.placeholderNode = null;
    this.moved = false;
    this.cd.detectChanges();
  }

  private positionPanel() {
    try {
      if (!this.moved || !this.panelRef || !this.selectContainer) return;

      const panelEl = this.panelRef.nativeElement as HTMLElement;
      const triggerEl = this.selectContainer.nativeElement as HTMLElement;
      const triggerRect = triggerEl.getBoundingClientRect();

      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const maxPanelHeight = 304;
      const gap = 6;

      let top = triggerRect.bottom + gap;
      const left = triggerRect.left;

      let adjustedMaxHeight = maxPanelHeight;

      if (spaceBelow < maxPanelHeight + gap) {
        adjustedMaxHeight = Math.max(150, spaceBelow - gap - 16);
      }

      panelEl.style.top = `${top}px`;
      panelEl.style.left = `${left}px`;
      panelEl.style.minWidth = `${Math.max(triggerRect.width, 160)}px`;
      panelEl.style.maxHeight = `${adjustedMaxHeight}px`;
      panelEl.style.overflowY = 'hidden';
    } catch (e) {
      // noop
    }
  }

  ngOnDestroy(): void {
    try {
      this.searchTermSubject.complete();
    } catch (e) {}
    // asegurarse de limpiar si quedó movido
    this.closePanel();
  }
}
